import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * VirtualJoystick — on-screen touch joystick for mobile users.
 * Only renders on touch devices. Positioned bottom-left.
 * Exposes normalized [-1,1] input via a shared ref.
 */

interface JoystickInput {
  x: number; // -1 (left) to 1 (right)
  z: number; // -1 (back) to 1 (forward)
}

// Shared ref — CharacterController reads this
export const joystickInput = { current: { x: 0, z: 0 } as JoystickInput };

export default function VirtualJoystick() {
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const baseRef = useRef<HTMLDivElement>(null);
  const center = useRef({ x: 0, y: 0 });
  const maxRadius = 40;

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (!baseRef.current) return;
    const rect = baseRef.current.getBoundingClientRect();
    center.current = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    setActive(true);
  }, []);

  const handleMove = useCallback((e: React.TouchEvent) => {
    if (!active) return;
    e.preventDefault();
    const touch = e.touches[0];
    let dx = touch.clientX - center.current.x;
    let dy = touch.clientY - center.current.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > maxRadius) {
      dx = (dx / dist) * maxRadius;
      dy = (dy / dist) * maxRadius;
    }

    setPos({ x: dx, y: dy });

    // Normalize to [-1, 1], invert Y since screen Y goes down
    joystickInput.current = {
      x: dx / maxRadius,
      z: -dy / maxRadius,
    };
  }, [active]);

  const handleEnd = useCallback(() => {
    setActive(false);
    setPos({ x: 0, y: 0 });
    joystickInput.current = { x: 0, z: 0 };
  }, []);

  if (!isTouchDevice) return null;

  return (
    <div
      className="fixed bottom-6 left-6 z-40 select-none"
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
      onTouchCancel={handleEnd}
    >
      {/* Base circle */}
      <div
        ref={baseRef}
        className="w-24 h-24 rounded-full bg-white/10 backdrop-blur-md border-2 border-white/20 flex items-center justify-center"
      >
        {/* Thumb stick */}
        <div
          className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm border border-white/40 transition-transform duration-75"
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px)`,
          }}
        />
      </div>
      <p className="text-white/30 text-[10px] text-center mt-1">Move</p>
    </div>
  );
}
